import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { grantCredits, listProfiles, updateTrialStatus } from "@/api/adminApi";
import { getMyProfile } from "@/api/profileApi";
import { useSupabaseSession } from "@/hooks/useSupabaseSession";
import type { TrialStatus } from "@/types";

const TrialStatusCell: React.FC<{
	profileId: string;
	currentStatus: TrialStatus | null;
	trialEndsAt: string | null;
}> = ({ profileId, currentStatus, trialEndsAt }) => {
	const queryClient = useQueryClient();
	const [status, setStatus] = useState<TrialStatus | "">(currentStatus || "");

	const mutation = useMutation({
		mutationFn: (newStatus: TrialStatus) => updateTrialStatus({ profileId, status: newStatus }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin", "profiles"] });
		},
	});

	const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const newStatus = e.target.value as TrialStatus;
		setStatus(newStatus);
		mutation.mutate(newStatus);
	};

	return (
		<div className="flex flex-col gap-1">
			<div className="flex items-center gap-2">
				<select
					value={status}
					onChange={handleChange}
					disabled={mutation.isPending}
					className="rounded border border-strong surface px-2 py-1 text-xs text-primary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
				>
					<option value="" disabled>
						Select...
					</option>
					<option value="PENDING">PENDING</option>
					<option value="ACTIVE">ACTIVE</option>
					<option value="EXPIRED">EXPIRED</option>
					<option value="CANCELED">CANCELED</option>
				</select>
				{mutation.isPending && <span className="h-2 w-2 animate-pulse rounded-full bg-yellow-400" />}
				{mutation.isSuccess && <span className="h-2 w-2 rounded-full bg-green-500" />}
				{mutation.isError && <span className="h-2 w-2 rounded-full bg-red-500" title="Error updating status" />}
			</div>
			{trialEndsAt && (
				<span className="text-[10px] text-subtle">Ends: {new Date(trialEndsAt).toLocaleDateString()}</span>
			)}
		</div>
	);
};

const AdminPage: React.FC = () => {
	const { session, isLoading: isSessionLoading } = useSupabaseSession();
	const navigate = useNavigate();
	const [profileId, setProfileId] = useState("");
	const [amount, setAmount] = useState("");
	const [graphId, setGraphId] = useState("");
	const [message, setMessage] = useState<{
		type: "success" | "error";
		text: string;
	} | null>(null);

	const { data: myProfile, isLoading: isProfileLoading } = useQuery({
		queryKey: ["profile", "me"],
		queryFn: ({ signal }) => getMyProfile({ signal }),
		enabled: !!session,
		retry: false,
	});

	const {
		data: profilesData,
		isLoading: isProfilesLoading,
		refetch: refetchProfiles,
		error: profilesError,
	} = useQuery({
		queryKey: ["admin", "profiles"],
		queryFn: ({ signal }) => listProfiles({ limit: 50 }, { signal }),
		enabled: !!myProfile && myProfile.profile.role === "ADMIN",
	});

	const grantMutation = useMutation({
		mutationFn: (body: Parameters<typeof grantCredits>[0]) => grantCredits(body),
		onSuccess: (data) => {
			setMessage({
				type: "success",
				text: `Successfully granted credits. New balance: ${data.creditBalance}`,
			});
			setProfileId("");
			setAmount("");
		},
		onError: (error) => {
			setMessage({
				type: "error",
				text: `Failed to grant credits: ${error instanceof Error ? error.message : "Unknown error"}`,
			});
		},
	});

	if (isSessionLoading || isProfileLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center surface-alt">
				<div className="card p-6">Loading...</div>
			</div>
		);
	}

	if (!session) {
		const next = encodeURIComponent(window.location.pathname + window.location.search);
		window.location.href = `/auth?next=${next}`;
		return null;
	}

	if (myProfile && myProfile.profile.role !== "ADMIN") {
		window.location.href = "/app";
		return null;
	}

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!profileId || !amount) return;

		setMessage(null);
		grantMutation.mutate({
			profileId,
			amountCredits: Number(amount),
		});
	};

	return (
		<div className="min-h-screen surface-alt">
			<div className="max-w-2xl mx-auto px-6 py-10">
				<div className="flex items-center justify-between">
					<h1 className="text-2xl font-semibold text-primary">Admin Dashboard</h1>
					<Link className="text-sm text-muted hover:text-primary" to="/app">
						Back
					</Link>
				</div>

				<div className="mt-6 card p-6">
					<h2 className="text-lg font-semibold text-primary mb-4">Open Knowledge Graph</h2>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							const id = graphId.trim();
							if (!id) return;
							navigate(`/graph?graphId=${encodeURIComponent(id)}`);
						}}
						className="space-y-4"
					>
						<div>
							<label htmlFor="graphId" className="block text-sm font-medium text-muted mb-1">
								Graph ID
							</label>
							<input
								id="graphId"
								type="text"
								value={graphId}
								onChange={(e) => setGraphId(e.target.value)}
								className="w-full rounded-md border border-strong surface px-3 py-2 text-sm text-primary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
								placeholder="Enter knowledge graph UUID"
								autoComplete="off"
								required
							/>
						</div>
						<button type="submit" className="btn btn-primary w-full justify-center">
							Open Visualization
						</button>
					</form>
				</div>

				<div className="mt-6 card p-6">
					<h2 className="text-lg font-semibold text-primary mb-4">Grant Credits</h2>

					{message && (
						<div
							className={`mb-4 p-3 rounded-md text-sm ${
								message.type === "success"
									? "bg-green-50 text-green-700 border border-green-200"
									: "bg-red-50 text-red-700 border border-red-200"
							}`}
						>
							{message.text}
						</div>
					)}

					<form onSubmit={handleSubmit} className="space-y-4">
						<div>
							<label htmlFor="profileId" className="block text-sm font-medium text-muted mb-1">
								Profile ID
							</label>
							<input
								id="profileId"
								type="text"
								value={profileId}
								onChange={(e) => setProfileId(e.target.value)}
								className="w-full rounded-md border border-strong surface px-3 py-2 text-sm text-primary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
								placeholder="Enter user profile ID"
								required
							/>
						</div>

						<div>
							<label htmlFor="amount" className="block text-sm font-medium text-muted mb-1">
								Amount
							</label>
							<input
								id="amount"
								type="number"
								value={amount}
								onChange={(e) => setAmount(e.target.value)}
								className="w-full rounded-md border border-strong surface px-3 py-2 text-sm text-primary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
								placeholder="e.g. 100"
								required
							/>
						</div>

						<button type="submit" disabled={grantMutation.isPending} className="btn btn-primary w-full justify-center">
							{grantMutation.isPending ? "Granting..." : "Grant Credits"}
						</button>
					</form>
				</div>

				<div className="mt-6 card p-6">
					<div className="mb-4 flex items-center justify-between">
						<h2 className="text-lg font-semibold text-primary">All Profiles</h2>
						<button
							type="button"
							onClick={() => refetchProfiles()}
							className="text-sm font-medium text-muted hover:text-primary"
						>
							Refresh
						</button>
					</div>

					{isProfilesLoading ? (
						<div className="text-sm text-subtle">Loading profiles...</div>
					) : profilesError ? (
						<div className="text-sm text-red-600">Failed to load profiles</div>
					) : (
						<div className="overflow-x-auto">
							<table className="w-full text-left text-sm">
								<thead className="border-b border-subtle text-subtle">
									<tr>
										<th className="py-2 pr-4 font-medium">ID</th>
										<th className="py-2 pr-4 font-medium">Role</th>
										<th className="py-2 pr-4 font-medium">Credits</th>
										<th className="py-2 pr-4 font-medium">Name</th>
										<th className="py-2 pr-4 font-medium">Trial Status</th>
										<th className="py-2 font-medium">Created</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-[var(--border)]">
									{profilesData?.items.map((profile) => (
										<tr key={profile.id} className="soft-hover">
											<td className="py-2 pr-4 font-mono text-xs text-muted">{profile.id}</td>
											<td className="py-2 pr-4">
												<span
													className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${
														profile.role === "ADMIN" ? "bg-purple-50 text-purple-700" : "surface-muted text-muted"
													}`}
												>
													{profile.role}
												</span>
											</td>
											<td className="py-2 pr-4 font-medium text-primary">{profile.creditBalance}</td>
											<td className="py-2 pr-4 text-muted">{profile.name || "-"}</td>
											<td className="py-2 pr-4">
												<TrialStatusCell
													profileId={profile.id}
													currentStatus={profile.trialStatus}
													trialEndsAt={profile.trialEndsAt}
												/>
											</td>
											<td className="py-2 text-xs text-subtle">{new Date(profile.createdAt).toLocaleDateString()}</td>
										</tr>
									))}
								</tbody>
							</table>
							{profilesData?.items.length === 0 && (
								<div className="py-4 text-center text-sm text-subtle">No profiles found.</div>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default AdminPage;
